import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatDate, initials, SOURCE_LABEL } from "@/lib/constants";
import {
  Search,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const RELATIONSHIP_TABS = [
  { key: "new", label: "Yeni Gelenler" },
  { key: "normal", label: "Normal Danışanlar" },
  { key: "special", label: "Özel Danışanlar" },
  { key: "all", label: "Tüm Kullanıcılar" },
];

const PRACTITIONER_FILTERS = [
  { key: "all", label: "Tüm uygulayıcılık durumları" },
  { key: "none", label: "Uygulayıcı değil" },
  { key: "candidate", label: "Uygulayıcı adayı" },
  { key: "level_1", label: "1. Seviye uygulayıcı" },
  { key: "level_2", label: "2. Seviye uygulayıcı" },
  { key: "level_3", label: "3. Seviye eğitmen" },
  { key: "mastery", label: "Ustalık seviyesi" },
];

function matchesAudience(client, relationship) {
  return relationship === "all" || client.client_relationship === relationship;
}

function matchesPractitioner(client, practitioner) {
  return practitioner === "all" || client.practitioner_category === practitioner;
}

function AudienceBadges({ client, kind }) {
  const levelLabels = {
    level_1: "1. Seviye Uygulayıcı",
    level_2: "2. Seviye Uygulayıcı",
    level_3: "3. Seviye Eğitmen",
    mastery: "Ustalık Seviyesi",
  };
  const relationshipLabels = {
    new: "Yeni Gelen",
    normal: "Normal Danışan",
    special: "Özel Danışan",
  };
  const relationshipStyles = {
    new: "bg-slate-50 text-slate-600 border-slate-200",
    normal: "bg-blue-50 text-blue-800 border-blue-200",
    special: "bg-emerald-50 text-emerald-800 border-emerald-200",
  };
  const practitioner = client.practitioner_category || "none";
  const practitionerLabel = practitioner === "candidate"
    ? "Uygulayıcı Adayı"
    : levelLabels[practitioner] || "Uygulayıcı Değil";
  if (kind === "relationship") {
    return <span className={`text-[10px] px-1.5 py-0.5 border ${relationshipStyles[client.client_relationship] || relationshipStyles.new}`}>
      {relationshipLabels[client.client_relationship] || relationshipLabels.new}
    </span>;
  }
  return <span className={`text-[10px] px-1.5 py-0.5 border ${practitioner === "none" ? "bg-slate-50 text-slate-600 border-slate-200" : "bg-violet-50 text-violet-800 border-violet-200"}`}>
    {practitionerLabel}
  </span>;
}

const PRACTITIONER_LEVELS = [
  { value: "level_1", label: "1. Seviye" },
  { value: "level_2", label: "2. Seviye" },
  { value: "level_3", label: "3. Seviye" },
  { value: "mastery", label: "Ustalık Seviyesi" },
];

function LevelSelect({ client, onLevelChange, compact = false }) {
  const current = client.practitioner_status === "approved"
    ? client.practitioner_approved_level || ""
    : "";
  return (
    <select
      value={current}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => {
        event.stopPropagation();
        onLevelChange(client.id, event.target.value);
      }}
      className={`${compact ? "h-7 text-[11px]" : "h-8 text-[12px]"} w-full px-2 border border-pleiades-border rounded-sm bg-white focus:outline-none focus:border-pleiades-blue`}
      aria-label={`${client.full_name} uygulayıcılık seviyesi`}
      data-testid={`client-level-${client.id}`}
    >
      <option value="" disabled>Seviye seçin</option>
      {PRACTITIONER_LEVELS.map((level) => (
        <option key={level.value} value={level.value}>{level.label}</option>
      ))}
    </select>
  );
}

function ClientsTable({ clients, onRowClick, onLevelChange, onDelete, canChangeLevel, selectedIds, onToggle, onToggleAll }) {
  return (
    <div className="border border-pleiades-border bg-white rounded-sm overflow-hidden" data-testid="clients-table">
      <div className="overflow-auto">
        <table className="w-full table-striped">
          <thead className="bg-pleiades-sidebar/60 border-b border-pleiades-border">
            <tr className="text-[11px] uppercase tracking-wider text-pleiades-slate">
              {canChangeLevel && (
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={clients.length > 0 && clients.every((client) => selectedIds.has(client.id))}
                    onChange={(event) => onToggleAll(event.target.checked)}
                    aria-label="Görünen kullanıcıların tümünü seç"
                    className="accent-pleiades-blue"
                  />
                </th>
              )}
              <th className="text-left font-medium px-3 py-2">Danışan</th>
              <th className="text-left font-medium px-3 py-2">İletişim</th>
              <th className="text-left font-medium px-3 py-2">Şehir</th>
              {canChangeLevel && <th className="text-left font-medium px-3 py-2 min-w-[150px]">Seviye Değiştir</th>}
              <th className="text-left font-medium px-3 py-2">Danışan durumu</th>
              <th className="text-left font-medium px-3 py-2">Uygulayıcılık</th>
              <th className="text-left font-medium px-3 py-2">Kaynak</th>
              <th className="text-left font-medium px-3 py-2">Etiketler</th>
              <th className="text-right font-medium px-3 py-2">Oluşturuldu</th>
              {canChangeLevel && <th className="text-right font-medium px-3 py-2">İşlem</th>}
            </tr>
          </thead>
          <tbody className="text-[13px]">
            {clients.length === 0 && (
              <tr>
                <td colSpan={canChangeLevel ? 11 : 8} className="text-center px-3 py-10 text-pleiades-slate text-[12px]">
                  Kayıt bulunamadı.
                </td>
              </tr>
            )}
            {clients.map((c) => (
              <tr
                key={c.id}
                onClick={() => onRowClick(c)}
                className="hover:bg-pleiades-blue-soft/40 cursor-pointer border-b border-pleiades-border/60"
                data-testid={`client-row-${c.id}`}
              >
                {canChangeLevel && (
                  <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={(event) => onToggle(c.id, event.target.checked)}
                      aria-label={`${c.full_name} kullanıcısını seç`}
                      className="accent-pleiades-blue"
                    />
                  </td>
                )}
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="pleiades-initials-avatar w-7 h-7 rounded-sm text-[11px] flex items-center justify-center font-medium shrink-0">
                      {initials(c.full_name)}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.full_name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-pleiades-slate">
                  <div className="text-[12px] truncate">{c.email || "—"}</div>
                  <div className="text-[11px] text-pleiades-muted">{c.phone || ""}</div>
                </td>
                <td className="px-3 py-2 text-pleiades-slate">{c.city || "—"}</td>
                {canChangeLevel && (
                  <td className="px-3 py-2">
                    <LevelSelect client={c} onLevelChange={onLevelChange} />
                  </td>
                )}
                <td className="px-3 py-2"><AudienceBadges client={c} kind="relationship" /></td>
                <td className="px-3 py-2"><AudienceBadges client={c} kind="practitioner" /></td>
                <td className="px-3 py-2 text-pleiades-slate">{SOURCE_LABEL[c.source] || c.source || "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(c.tags || []).slice(0, 3).map((t, i) => (
                      <span
                        key={i}
                        className="text-[10px] px-1.5 py-0.5 bg-pleiades-gold-soft text-[#8a6f37] border border-pleiades-gold/30 rounded-sm"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2 text-right text-[12px] text-pleiades-muted tabular-nums">
                  {formatDate(c.created_at)}
                </td>
                {canChangeLevel && (
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete(c);
                      }}
                      className="inline-flex min-h-9 items-center gap-1.5 border border-red-200 bg-white px-2.5 text-[11px] font-medium text-red-700 hover:bg-red-50"
                      aria-label={`${c.full_name} kullanıcısını kalıcı olarak sil`}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                      Sil
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [relationship, setRelationship] = useState("new");
  const [practitioner, setPractitioner] = useState("all");
  const [deletingId, setDeletingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canChangeLevel = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      const { data } = await api.get("/clients", { params });
      setClients(data);
      setSelectedIds((current) => new Set([...current].filter((id) => data.some((client) => client.id === id))));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const onLevelChange = async (id, level) => {
    const previous = clients.find((client) => client.id === id);
    setClients((items) => items.map((client) => (
      client.id === id
        ? { ...client, practitioner_status: "approved", practitioner_approved_level: level }
        : client
    )));
    try {
      await api.patch(`/clients/${id}/practitioner-level`, { level });
      const label = PRACTITIONER_LEVELS.find((item) => item.value === level)?.label;
      toast.success(`Uygulayıcılık seviyesi ${label} olarak güncellendi`);
    } catch (error) {
      if (previous) {
        setClients((items) => items.map((client) => client.id === id ? previous : client));
      }
      toast.error(formatApiError(error));
    }
  };

  const onRowClick = (c) => navigate(`/danisanlar/${c.id}`);
  const onToggle = (id, checked) => setSelectedIds((current) => {
    const next = new Set(current);
    if (checked) next.add(id); else next.delete(id);
    return next;
  });
  const onDelete = async (client) => {
    if (deletingId) return;
    const accepted = window.confirm(
      `${client.full_name} kalıcı olarak silinecek. Hesabı, davetleri, anketleri, çalışmalar, deneyimler ve arşiv kayıtları geri alınamaz biçimde kaldırılacak. Kullanıcı daha sonra sıfırdan kayıt olabilir. Devam edilsin mi?`,
    );
    if (!accepted) return;
    const confirmedAgain = window.confirm(
      "Bu işlem geri alınamaz. Kullanıcıyı ve tüm çalışmalarını kesin olarak silmek istiyor musunuz?",
    );
    if (!confirmedAgain) return;
    setDeletingId(client.id);
    try {
      await api.delete(`/clients/${client.id}`);
      setClients((items) => items.filter((item) => item.id !== client.id));
      toast.success("Kullanıcı ve bağlı tüm çalışma kayıtları silindi");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
    }
  };
  const onBulkDelete = async () => {
    if (!selectedIds.size || bulkDeleting || deletingId) return;
    const names = clients.filter((client) => selectedIds.has(client.id)).map((client) => client.full_name);
    const confirmation = window.prompt(
      `${names.length} kullanıcı ve tüm bağlı çalışmaları kalıcı olarak silinecek. İşlemi onaylamak için SİL yazın.`,
    );
    if (confirmation?.trim().toLocaleUpperCase("tr-TR") !== "SİL") return;
    setBulkDeleting(true);
    try {
      const ids = [...selectedIds];
      await api.post("/clients/bulk-delete", { client_ids: ids });
      setClients((items) => items.filter((item) => !selectedIds.has(item.id)));
      setSelectedIds(new Set());
      toast.success(`${ids.length} kullanıcı ve bağlı kayıtları silindi`);
    } catch (error) {
      toast.error(formatApiError(error));
      await load();
    } finally {
      setBulkDeleting(false);
    }
  };
  const visibleClients = useMemo(
    () => clients.filter((client) => (
      matchesAudience(client, relationship) && matchesPractitioner(client, practitioner)
    )),
    [clients, relationship, practitioner]
  );
  const audienceCounts = useMemo(
    () => Object.fromEntries(RELATIONSHIP_TABS.map((item) => [
      item.key,
      clients.filter((client) => matchesAudience(client, item.key)).length,
    ])),
    [clients]
  );

  return (
    <div className="p-3 sm:p-5 lg:p-6" data-testid="clients-page">
      <div className="flex items-center justify-between mb-3 sm:mb-4 flex-wrap gap-2 sm:gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Danışanlar</h1>
          <p className="text-[13px] text-pleiades-slate mt-0.5">
            {RELATIONSHIP_TABS.find((item) => item.key === relationship)?.label}: {visibleClients.length} kayıt
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-3 sm:mb-4 overflow-x-auto pb-1">
        {RELATIONSHIP_TABS.map((item) => (
          <button key={item.key} onClick={() => setRelationship(item.key)}
            className={`h-9 px-3 shrink-0 border text-[12px] ${relationship === item.key ? "bg-pleiades-ink text-white border-pleiades-ink" : "bg-white border-pleiades-border text-pleiades-slate hover:text-pleiades-ink"}`}>
            {item.label} <span className={`ml-1 ${relationship === item.key ? "text-white/70" : "text-pleiades-muted"}`}>{audienceCounts[item.key] || 0}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-3 sm:mb-4 flex-wrap">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-pleiades-muted absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ad, e-posta, telefon ara…"
            data-testid="clients-search"
            className="h-8 pl-8 pr-3 w-64 border border-pleiades-border rounded-sm text-[12px] bg-white focus:outline-none focus:border-pleiades-blue"
          />
        </div>
        <select
          value={practitioner}
          onChange={(event) => setPractitioner(event.target.value)}
          className="h-8 min-w-52 border border-pleiades-border bg-white px-2.5 text-[12px] text-pleiades-slate focus:outline-none focus:border-pleiades-blue"
          aria-label="Uygulayıcılık durumuna göre filtrele"
          data-testid="clients-practitioner-filter"
        >
          {PRACTITIONER_FILTERS.map((item) => (
            <option key={item.key} value={item.key}>{item.label}</option>
          ))}
        </select>
        {q && (
          <button
            onClick={() => setQ("")}
            className="h-8 px-2 text-[12px] text-pleiades-slate hover:text-pleiades-ink flex items-center gap-1"
            data-testid="clients-clear-filters"
          >
            <X className="w-3 h-3" /> Temizle
          </button>
        )}
        {canChangeLevel && selectedIds.size > 0 && (
          <button
            type="button"
            onClick={onBulkDelete}
            disabled={bulkDeleting}
            className="h-9 px-3 inline-flex items-center gap-2 border border-red-300 bg-white text-[12px] font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 size={14} aria-hidden="true" />
            {bulkDeleting ? "Siliniyor…" : `Seçilenleri sil (${selectedIds.size})`}
          </button>
        )}
      </div>

      {loading ? (
        <div className="h-40 border border-pleiades-border bg-white rounded-sm animate-pulse" />
      ) : (
        <ClientsTable
          clients={visibleClients}
          onRowClick={onRowClick}
          onLevelChange={onLevelChange}
          onDelete={onDelete}
          canChangeLevel={canChangeLevel}
          selectedIds={selectedIds}
          onToggle={onToggle}
          onToggleAll={(checked) => setSelectedIds((current) => {
            const next = new Set(current);
            visibleClients.forEach((client) => checked ? next.add(client.id) : next.delete(client.id));
            return next;
          })}
        />
      )}

    </div>
  );
}
